import React, { useEffect, useRef, useCallback } from 'react';
import { Chart, registerables, type Plugin, type LegendItem } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { GHGInventoryResult } from './GHGInventorySupabase';
import { GHG_BUILDING_ENERGY } from '../../theme/navChartPalette';
import { GHG_INVENTORY_CHART_HEIGHT_PX } from '../../theme/ghgInventoryChartLayout';
import { downloadChartJsInstanceAsPNG, downloadDataAsCSV, formatFilename } from '../../utils/downloadUtils';
import { flattenSectorGhgInventory } from '../../services/ghgInventoryDownloadService';

Chart.register(...registerables);

/** Draw sector % on pie slices (legend carries full sector names + %). */
const sectorPieSliceLabelsPlugin: Plugin<'pie'> = {
  id: 'sectorPieSliceLabels',
  afterDatasetsDraw(chart) {
    const meta = chart.getDatasetMeta(0);
    if (!meta?.data?.length) return;
    const raw = chart.data.datasets[0]?.data;
    const values = (Array.isArray(raw) ? raw : []).map((n) => Number(n) || 0);
    const total = values.reduce((a, b) => a + b, 0);
    if (total <= 0) return;

    const ctx = chart.ctx;
    const labels = (chart.data.labels ?? []) as string[];
    const minPctOnSlice = 4;

    meta.data.forEach((elem, i) => {
      const v = values[i] ?? 0;
      const pct = (v / total) * 100;
      if (pct < minPctOnSlice || v <= 0) return;
      const arc = elem as unknown as { tooltipPosition: (useFinalPosition?: boolean) => { x: number; y: number } };
      const { x, y } = arc.tooltipPosition(true);
      const sector = String(labels[i] ?? '');
      const short =
        sector.length > 22 ? `${sector.slice(0, 20)}…` : sector;
      const line1 = short;
      const line2 = `${pct.toFixed(1)}%`;

      ctx.save();
      ctx.font = '600 11px system-ui, -apple-system, Segoe UI, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.lineWidth = 3;
      ctx.strokeStyle = 'rgba(255,255,255,0.92)';
      ctx.fillStyle = '#0f172a';
      const dy = 7;
      ctx.strokeText(line1, x, y - dy);
      ctx.fillText(line1, x, y - dy);
      ctx.font = '700 12px system-ui, -apple-system, Segoe UI, sans-serif';
      ctx.strokeText(line2, x, y + dy);
      ctx.fillText(line2, x, y + dy);
      ctx.restore();
    });
  },
};

type ChartType = 'stacked-area' | 'line' | 'pie';

interface SectorBasedGHGInventoryProps {
  location: string;
  chartType: ChartType;
  setChartType: (chartType: ChartType) => void;
  ghgData?: GHGInventoryResult | null;
  onDownloadPNGReady?: (handler: () => void) => void;
  onDownloadCSVReady?: (handler: () => void) => void;
}

export const SectorBasedGHGInventory: React.FC<SectorBasedGHGInventoryProps> = ({
  location,
  chartType,
  setChartType,
  ghgData,
  onDownloadPNGReady,
  onDownloadCSVReady,
}) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  // Use real data from props, fallback to sample data if not available
  const sampleData: ChartDataPoint[] = [
    { year: 2005, lightDutyVehicles: 410000, heavyDutyVehicles: 50000, naturalGasResidential: 83000, electricityCommercial: 48000, electricityResidential: 42000, naturalGasCommercial: 38000, waste: 18000, offroad: 15000 },
    { year: 2008, lightDutyVehicles: 395000, heavyDutyVehicles: 48000, naturalGasResidential: 81000, electricityCommercial: 46000, electricityResidential: 40000, naturalGasCommercial: 36000, waste: 17000, offroad: 14500 },
    { year: 2011, lightDutyVehicles: 375000, heavyDutyVehicles: 46000, naturalGasResidential: 78000, electricityCommercial: 44000, electricityResidential: 38000, naturalGasCommercial: 34000, waste: 16000, offroad: 14000 },
    { year: 2014, lightDutyVehicles: 355000, heavyDutyVehicles: 44000, naturalGasResidential: 75000, electricityCommercial: 42000, electricityResidential: 36000, naturalGasCommercial: 32000, waste: 16000, offroad: 13500 },
    { year: 2017, lightDutyVehicles: 335000, heavyDutyVehicles: 42000, naturalGasResidential: 72000, electricityCommercial: 40000, electricityResidential: 34000, naturalGasCommercial: 30000, waste: 15000, offroad: 13000 },
    { year: 2020, lightDutyVehicles: 315000, heavyDutyVehicles: 40000, naturalGasResidential: 69000, electricityCommercial: 38000, electricityResidential: 32000, naturalGasCommercial: 28000, waste: 15000, offroad: 12500 },
    { year: 2023, lightDutyVehicles: 295000, heavyDutyVehicles: 38000, naturalGasResidential: 66000, electricityCommercial: 36000, electricityResidential: 30000, naturalGasCommercial: 26000, waste: 15000, offroad: 12000 }
  ];

  // Define type for chart data points
  type ChartDataPoint = {
    year: number;
    lightDutyVehicles: number;
    heavyDutyVehicles: number;
    naturalGasResidential: number;
    electricityCommercial: number;
    electricityResidential: number;
    naturalGasCommercial: number;
    waste: number;
    offroad: number;
  };

  // Convert real data to chart format if available
  const chartData: ChartDataPoint[] = ghgData ? (() => {
    const years = ghgData.chartData.labels;
    const datasets = ghgData.chartData.datasets;
    
    return years.map((year: any, index: number) => {
      const dataPoint: ChartDataPoint = { 
        year: parseInt(year),
        lightDutyVehicles: 0,
        heavyDutyVehicles: 0,
        naturalGasResidential: 0,
        electricityCommercial: 0,
        electricityResidential: 0,
        naturalGasCommercial: 0,
        waste: 0,
        offroad: 0
      };
      datasets.forEach((dataset: any) => {
        const value = dataset.data[index] || 0;
        // Map dataset labels to our expected property names
        switch (dataset.label) {
          case 'Light Duty Vehicles':
            dataPoint.lightDutyVehicles = value;
            break;
          case 'Heavy Duty Vehicles':
            dataPoint.heavyDutyVehicles = value;
            break;
          case 'Natural Gas Residential':
            dataPoint.naturalGasResidential = value;
            break;
          case 'Electricity Commercial':
          case 'Electricity Nonresidential':
            dataPoint.electricityCommercial = value;
            break;
          case 'Electricity Residential':
            dataPoint.electricityResidential = value;
            break;
          case 'Natural Gas Commercial':
            dataPoint.naturalGasCommercial = value;
            break;
          case 'Waste':
          case 'Waste & wastewater':
            dataPoint.waste = value;
            break;
          case 'Offroad':
            dataPoint.offroad = value;
            break;
        }
      });
      return dataPoint;
    });
  })() : sampleData;

  const createChart = useCallback(() => {
    if (!chartRef.current) return;

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    // Destroy existing chart
    if (chartInstance.current) {
      chartInstance.current.destroy();
      chartInstance.current = null;
    }

    // Clear the canvas
    if (ctx) {
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    }

    let chartConfig: any;

    if (chartType === 'pie') {
      const PIE_YEAR = 2023;
      type PieSlice = { label: string; value: number; backgroundColor: string };
      let slices: PieSlice[] = [];
      let pieYearLabel: number = PIE_YEAR;

      if (ghgData?.chartData?.datasets?.length && ghgData.chartData.labels?.length) {
        const yearArr = (ghgData.chartData.labels as unknown[]).map((y) => Number(y));
        let idx = yearArr.indexOf(PIE_YEAR);
        if (idx < 0) idx = Math.max(0, yearArr.length - 1);
        pieYearLabel = yearArr[idx] ?? PIE_YEAR;
        slices = ghgData.chartData.datasets.map((ds: { label?: string; data?: unknown[]; backgroundColor?: unknown }) => {
          const v = Number(ds.data?.[idx]) || 0;
          const bg = ds.backgroundColor;
          const backgroundColor = typeof bg === 'string' ? bg : '#94a3b8';
          return { label: String(ds.label ?? ''), value: v, backgroundColor };
        });
      } else {
        const pieData = chartData[chartData.length - 1];
        pieYearLabel = pieData.year;
        slices = [
          { label: 'Light Duty Vehicles', value: pieData.lightDutyVehicles, backgroundColor: '#3b82f6' },
          { label: 'Heavy Duty Vehicles', value: pieData.heavyDutyVehicles, backgroundColor: '#60a5fa' },
          { label: 'Natural Gas Residential', value: pieData.naturalGasResidential, backgroundColor: GHG_BUILDING_ENERGY.naturalGasResidential.pieHex },
          { label: 'Electricity Commercial', value: pieData.electricityCommercial, backgroundColor: GHG_BUILDING_ENERGY.electricityCommercial.pieHex },
          { label: 'Electricity Residential', value: pieData.electricityResidential, backgroundColor: GHG_BUILDING_ENERGY.electricityResidential.pieHex },
          { label: 'Natural Gas Commercial', value: pieData.naturalGasCommercial, backgroundColor: GHG_BUILDING_ENERGY.naturalGasCommercial.pieHex },
          { label: 'Waste & wastewater', value: pieData.waste, backgroundColor: '#ec4899' },
          { label: 'Offroad', value: pieData.offroad, backgroundColor: '#FFD200' },
        ];
      }

      const positive = slices.filter((s) => s.value > 0);

      chartConfig = {
        type: 'pie',
        plugins: [sectorPieSliceLabelsPlugin],
        data: {
          labels: positive.map((s) => s.label),
          datasets: [
            {
              data: positive.map((s) => s.value),
              backgroundColor: positive.map((s) => s.backgroundColor),
              borderColor: '#ffffff',
              borderWidth: 2,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: { top: 20, bottom: 8, left: 14, right: 14 },
          },
          plugins: {
            title: {
              display: true,
              text: `Sector GHG mix (${pieYearLabel}) - ${location}`,
              font: { size: 14, weight: 'bold' },
            },
            legend: {
              position: 'bottom',
              labels: {
                usePointStyle: true,
                padding: 10,
                boxWidth: 10,
                font: { size: 11 },
                generateLabels: (chart: Chart<'pie'>): LegendItem[] => {
                  const ds = chart.data.datasets[0];
                  const data = (ds?.data as number[]) || [];
                  const labels = (chart.data.labels || []).map((l: unknown) => String(l));
                  const sum = data.reduce((a, b) => a + (Number(b) || 0), 0);
                  return labels.map((label: string, i: number) => {
                    const raw = Number(data[i]) || 0;
                    const pct = sum > 0 ? ((raw / sum) * 100).toFixed(1) : '0.0';
                    const fill = (Array.isArray(ds.backgroundColor) ? ds.backgroundColor[i] : ds.backgroundColor) as
                      | string
                      | undefined;
                    return {
                      text: `${label}: ${pct}%`,
                      fillStyle: fill || '#64748b',
                      fontColor: '#334155',
                      hidden: false,
                      index: i,
                      datasetIndex: 0,
                    };
                  });
                },
              },
            },
            tooltip: {
              callbacks: {
                label: (ctx: { label?: string; raw: unknown; dataset: { data?: unknown[] } }) => {
                  const label = ctx.label || '';
                  const value = Number(ctx.raw) || 0;
                  const data = (ctx.dataset.data as number[]) || [];
                  const total = data.reduce((a, b) => a + (Number(b) || 0), 0);
                  const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                  return `${label}: ${value.toLocaleString()} MT CO₂e (${percentage}% of ${pieYearLabel} total)`;
                },
              },
            },
          },
        },
      };
    } else {
      // Line or stacked area chart for time series
      const isStacked = chartType === 'stacked-area';
      
      chartConfig = {
        type: 'line', // Always use line type, control stacking via fill and stack properties
        data: {
          labels: chartData.map(d => d.year),
          datasets: [
            {
              label: 'Light Duty Vehicles',
              data: chartData.map(d => d.lightDutyVehicles),
              borderColor: 'rgba(59, 130, 246, 0.8)',  // Match background for solid look
              backgroundColor: isStacked ? 'rgba(59, 130, 246, 0.8)' : 'rgba(59, 130, 246, 0.1)',
              borderWidth: isStacked ? 0 : 2,  // No border for stacked, keep for line chart
              fill: isStacked,
              tension: 0,  // No line smoothing - straight lines
              pointRadius: 0,
              stack: isStacked ? 'stack1' : undefined
            },
            {
              label: 'Heavy Duty Vehicles',
              data: chartData.map(d => d.heavyDutyVehicles),
              borderColor: 'rgba(96, 165, 250, 0.8)',  // Match background for solid look
              backgroundColor: isStacked ? 'rgba(96, 165, 250, 0.8)' : 'rgba(96, 165, 250, 0.1)',
              borderWidth: isStacked ? 0 : 2,  // No border for stacked, keep for line chart
              fill: isStacked,
              tension: 0,  // No line smoothing - straight lines
              pointRadius: 0,
              stack: isStacked ? 'stack1' : undefined
            },
            {
              label: 'Natural Gas - Residential',
              data: chartData.map(d => d.naturalGasResidential),
              borderColor: GHG_BUILDING_ENERGY.naturalGasResidential.bg08,
              backgroundColor: isStacked ? GHG_BUILDING_ENERGY.naturalGasResidential.bg08 : GHG_BUILDING_ENERGY.naturalGasResidential.bg01,
              borderWidth: isStacked ? 0 : 2,  // No border for stacked, keep for line chart
              fill: isStacked,
              tension: 0,  // No line smoothing - straight lines
              pointRadius: 0,
              stack: isStacked ? 'stack1' : undefined
            },
            {
              label: 'Natural Gas - Commercial',
              data: chartData.map(d => d.naturalGasCommercial),
              borderColor: GHG_BUILDING_ENERGY.naturalGasCommercial.bg08,
              backgroundColor: isStacked ? GHG_BUILDING_ENERGY.naturalGasCommercial.bg08 : GHG_BUILDING_ENERGY.naturalGasCommercial.bg01,
              borderWidth: isStacked ? 0 : 2,  // No border for stacked, keep for line chart
              fill: isStacked,
              tension: 0,  // No line smoothing - straight lines
              pointRadius: 0,
              stack: isStacked ? 'stack1' : undefined
            },
            {
              label: 'Electricity - Residential',
              data: chartData.map(d => d.electricityResidential),
              borderColor: GHG_BUILDING_ENERGY.electricityResidential.bg08,
              backgroundColor: isStacked ? GHG_BUILDING_ENERGY.electricityResidential.bg08 : GHG_BUILDING_ENERGY.electricityResidential.bg01,
              borderWidth: isStacked ? 0 : 2,  // No border for stacked, keep for line chart
              fill: isStacked,
              tension: 0,  // No line smoothing - straight lines
              pointRadius: 0,
              stack: isStacked ? 'stack1' : undefined
            },
            {
              label: 'Electricity - Commercial',
              data: chartData.map(d => d.electricityCommercial),
              borderColor: GHG_BUILDING_ENERGY.electricityCommercial.bg08,
              backgroundColor: isStacked ? GHG_BUILDING_ENERGY.electricityCommercial.bg08 : GHG_BUILDING_ENERGY.electricityCommercial.bg01,
              borderWidth: isStacked ? 0 : 2,  // No border for stacked, keep for line chart
              fill: isStacked,
              tension: 0,  // No line smoothing - straight lines
              pointRadius: 0,
              stack: isStacked ? 'stack1' : undefined
            },
            {
              label: 'Waste & wastewater',
              data: chartData.map(d => d.waste),
              borderColor: 'rgba(236, 72, 153, 0.8)',  // Match background for solid look
              backgroundColor: isStacked ? 'rgba(236, 72, 153, 0.8)' : 'rgba(236, 72, 153, 0.1)',
              borderWidth: isStacked ? 0 : 2,  // No border for stacked, keep for line chart
              fill: isStacked,
              tension: 0,  // No line smoothing - straight lines
              pointRadius: 0,
              stack: isStacked ? 'stack1' : undefined
            },
            {
              label: 'Offroad',
              data: chartData.map(d => d.offroad),
              borderColor: 'rgba(255, 210, 0, 0.8)',  // Match background for solid look
              backgroundColor: isStacked ? 'rgba(255, 210, 0, 0.8)' : 'rgba(255, 210, 0, 0.1)',
              borderWidth: isStacked ? 0 : 2,  // No border for stacked, keep for line chart
              fill: isStacked,
              tension: 0,  // No line smoothing - straight lines
              pointRadius: 0,
              stack: isStacked ? 'stack1' : undefined
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: {
              top: 10,
              bottom: 10,
              left: 10,
              right: 10
            }
          },
          plugins: {
            title: {
              display: true,
              text:
                chartType === 'stacked-area'
                  ? `Sector-based GHG (stacked area) - ${location}`
                  : `Sector-based GHG (line) - ${location}`,
              font: { size: 14, weight: 'bold' },
            },
            legend: {
              position: 'bottom',
              labels: {
                usePointStyle: true,
                padding: 15,
                font: {
                  size: 13
                }
              }
            }
          },
          scales: {
            x: {
              stacked: isStacked,
              title: {
                display: true,
                text: 'Year',
                font: {
                  size: 13,
                  weight: ('bold' as const)
                },
                padding: { top: 10, bottom: 5 }
              },
              ticks: {
                font: {
                  size: 12
                },
                maxRotation: 45,
                minRotation: 45
              },
              grid: {
                display: false
              }
            },
            y: {
              stacked: isStacked,
              title: {
                display: true,
                text: 'GHG Emissions (MT CO₂e)',
                font: {
                  size: 13,
                  weight: ('bold' as const)
                },
                padding: { top: 5, bottom: 10 }
              },
              beginAtZero: true,
              grid: {
                color: 'rgba(0, 0, 0, 0.05)'
              },
              ticks: {
                font: {
                  size: 12
                }
              }
            }
          },
          interaction: {
            intersect: false,
            mode: 'index'
          }
        }
      };
    }

    // Create new chart instance
    chartInstance.current = new Chart(ctx, chartConfig);
  }, [chartType, chartData, ghgData, location]);

  useEffect(() => {
    // Ensure canvas ref is ready before creating chart
    if (!chartRef.current) {
      // Try again after a short delay to allow canvas to mount
      const timeoutId = setTimeout(() => {
        if (chartRef.current) {
          createChart();
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }
    
    createChart();

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [chartType, location, createChart]);

  const chartTypeLabel =
    chartType === 'stacked-area' ? 'Stacked_Area' : chartType === 'pie' ? 'Pie' : 'Line';

  const handleDownloadPNG = useCallback(() => {
    downloadChartJsInstanceAsPNG(
      chartInstance.current,
      formatFilename(location, `Sector_Based_GHG_${chartTypeLabel}`)
    );
  }, [location, chartTypeLabel]);

  const handleDownloadCSV = useCallback(() => {
    if (!ghgData) return;
    downloadDataAsCSV(
      flattenSectorGhgInventory(location, ghgData),
      formatFilename(location, 'Sector_Based_GHG_Inventory')
    );
  }, [location, ghgData]);

  useEffect(() => {
    onDownloadPNGReady?.(handleDownloadPNG);
  }, [onDownloadPNGReady, handleDownloadPNG]);

  useEffect(() => {
    onDownloadCSVReady?.(handleDownloadCSV);
  }, [onDownloadCSVReady, handleDownloadCSV]);

  return (
    <div style={{ 
      width: '100%', 
      height: GHG_INVENTORY_CHART_HEIGHT_PX,
      minHeight: GHG_INVENTORY_CHART_HEIGHT_PX,
      position: 'relative',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <canvas ref={chartRef} key={chartType} />
    </div>
  );
};
